import { useState, useMemo } from "react";
import GraficasMetricas from "./GraficasMetricas";

// ── Badge de escenario ácido para la tabla ─────────────────────────────────
const PARAMS_KEY     = "panel_esc_params";
const DEFAULT_PARAMS = { deltaAcid: 10, deltaOpt: 10, factorOpt: 0.8 };

function loadParams() {
  try { return { ...DEFAULT_PARAMS, ...JSON.parse(localStorage.getItem(PARAMS_KEY)) }; }
  catch { return DEFAULT_PARAMS; }
}

function gananciaAcida(p, delta) {
  const TC_real   = p.tasa_confirmacion || 0;
  const TE_real   = p.tasa_entrega      || 0;
  const TC_acid   = Math.max(TC_real - delta, 5);
  const TE_acid   = Math.max(TE_real - delta, 5);
  const ratio_r   = (TC_real / 100) * (TE_real / 100);
  const ratio_a   = (TC_acid / 100) * (TE_acid / 100);
  const scale     = ratio_r > 0 ? ratio_a / ratio_r : 0;
  const gasto     = p.gasto_meta  || 0;
  const msm       = (p.margen_real || 0) + gasto; // margen sin Meta
  return msm * scale - gasto;
}

function BadgeEscenario({ p }) {
  const { deltaAcid } = loadParams();
  const gan = gananciaAcida(p, deltaAcid);
  const ingr = Math.abs(p.ingresos_netos || 1);
  const umbral = ingr * 0.05; // dentro del ±5% de ingresos = "riesgo"

  let label, bg, color;
  if (gan > 0)          { label = "Sobrevive";  bg = "#D1FAE5"; color = "#065F46"; }
  else if (gan > -umbral) { label = "Riesgo";     bg = "#FEF3C7"; color = "#92400E"; }
  else                    { label = "No viable";  bg = "#FEE2E2"; color = "#991B1B"; }

  return (
    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600, bg, background: bg, color, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

const pillStyle = {
  escalar:   { background: "#EAF3DE", color: "#3B6D11" },
  apagar:    { background: "#FCEBEB", color: "#A32D2D" },
  vigilar:   { background: "#FAEEDA", color: "#854F0B" },
  reactivar: { background: "#E6F1FB", color: "#185FA5" },
  inactiva:  { background: "#f0f0ee", color: "#666" },
};
const pillLabel = {
  escalar: "🟢 Escalar", apagar: "🔴 Apagar", vigilar: "🟡 Vigilar",
  reactivar: "⚪ Reactivar", inactiva: "⚫ Inactiva",
};

// Criterio legible para cada acción estratégica
const pillCriterio = {
  escalar:   "Tasa entrega ≥70% · Margen real positivo · ROAS ≥1.3",
  apagar:    "Margen real negativo y tasa entrega <50%, o ROAS <0.5",
  vigilar:   "Tasa entrega 50–70% · Margen bajo o ROAS entre 0.5 y 1.3",
  reactivar: "Sin pedidos recientes pero con historial de margen positivo",
  inactiva:  "Sin actividad registrada en el período",
};

const fmtMargen = n => {
  if (!n && n !== 0) return <span style={{ color: "#aaa" }}>—</span>;
  const abs = "$" + Math.abs(Math.round(n)).toLocaleString("es-CL");
  return <span style={{ color: n >= 0 ? "#3B6D11" : "#A32D2D" }}>{n < 0 ? "-" : ""}{abs}</span>;
};
const colorPct = (n, bueno, medio) =>
  n >= bueno ? "#3B6D11" : n >= medio ? "#854F0B" : "#A32D2D";

const FILTROS = [
  { key: "all",       label: "Todos" },
  { key: "escalar",   label: "🟢 Escalar" },
  { key: "vigilar",   label: "🟡 Vigilar" },
  { key: "apagar",    label: "🔴 Apagar" },
  { key: "reactivar", label: "⚪ Reactivar" },
  { key: "inactiva",  label: "⚫ Inactiva" },
];

// Tooltip simple (sin librería)
function TooltipAccion({ accion }) {
  const [visible, setVisible] = useState(false);
  const criterio = pillCriterio[accion];
  if (!criterio) return null;
  return (
    <span style={{ position:"relative", display:"inline-block", marginLeft:4, verticalAlign:"middle" }}>
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        style={{ fontSize:9, color:"#bbb", cursor:"help", border:"0.5px solid #ddd",
          borderRadius:"50%", width:13, height:13, display:"inline-flex",
          alignItems:"center", justifyContent:"center", fontWeight:600, lineHeight:1 }}>
        ?
      </span>
      {visible && (
        <div style={{ position:"absolute", bottom:"calc(100% + 6px)", left:"50%",
          transform:"translateX(-50%)", background:"#222", color:"#fff",
          fontSize:10, borderRadius:7, padding:"5px 9px", whiteSpace:"nowrap",
          zIndex:100, boxShadow:"0 4px 12px #0003", lineHeight:1.5,
          maxWidth:240, whiteSpace:"normal" }}>
          <div style={{ fontWeight:600, marginBottom:2 }}>Criterio</div>
          {criterio}
        </div>
      )}
    </span>
  );
}

export default function AnalisisEstrategico({ productos }) {
  const [busqueda, setBusqueda] = useState("");
  const [filtro,   setFiltro]   = useState("all");

  const productosFiltrados = useMemo(() => {
    return productos.filter(p => {
      const coincideBusqueda = p.nombre?.toLowerCase().includes(busqueda.toLowerCase());
      const coincideFiltro   = filtro === "all" || p.accion_codigo === filtro;
      return coincideBusqueda && coincideFiltro;
    });
  }, [productos, busqueda, filtro]);

  const btnStyle = active => ({
    fontSize: 11, padding: "4px 12px", borderRadius: 20, cursor: "pointer",
    border: active ? "none" : "0.5px solid #ddd",
    background: active ? "#111" : "transparent",
    color: active ? "#fff" : "#666",
  });

  return (
    <div>
      <GraficasMetricas productos={productos} />

      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem 1.1rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: "0.9rem" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Panel estratégico · Productos
          </div>
          <input
            type="text"
            placeholder="Buscar producto..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{ fontSize: 12, padding: "6px 12px", borderRadius: 8,
              border: "0.5px solid #ddd", outline: "none", width: 200, background: "#fafaf9" }}
          />
        </div>

        {/* Filtros */}
        <div style={{ display: "flex", gap: 6, marginBottom: "0.85rem", flexWrap: "wrap" }}>
          {FILTROS.map(f => (
            <button key={f.key} onClick={() => setFiltro(f.key)} style={btnStyle(filtro === f.key)}>
              {f.label}
            </button>
          ))}
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Producto", "Pedidos", "Conf.%", "Entrega%", "Fallidos", "CPA Real", "ROAS", "Margen Real", "Margen Pot.", "Escenario", "Acción"].map(h => (
                  <th key={h} style={{ textAlign: "left", color: "#888", fontWeight: 400, fontSize: 10,
                    textTransform: "uppercase", letterSpacing: "0.05em",
                    padding: "0 6px 7px 0", borderBottom: "0.5px solid #eee", whiteSpace: "nowrap" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {productosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: "1.5rem 0", color: "#aaa", fontSize: 12, textAlign: "center" }}>
                    Sin resultados para "{busqueda}"
                  </td>
                </tr>
              ) : productosFiltrados.map((p, i) => {
                const accion = p.accion_codigo || "inactiva";
                return (
                  <tr key={i}>
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee",
                      maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      title={p.nombre}>{p.nombre}</td>
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee" }}>{p.total_pedidos}</td>
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee",
                      color: colorPct(p.tasa_confirmacion, 75, 60) }}>{p.tasa_confirmacion?.toFixed(1)}%</td>
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee",
                      color: colorPct(p.tasa_entrega, 75, 60) }}>{p.tasa_entrega?.toFixed(1)}%</td>
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee" }}>{p.fallidos}</td>

                    {/* CPA Real */}
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee" }}>
                      {p.cpa_real > 0
                        ? "$" + Math.round(p.cpa_real).toLocaleString("es-CL")
                        : <span style={{ color: "#bbb", fontSize: 11 }}>Sin campaña activa</span>}
                    </td>

                    {/* ROAS */}
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee",
                      color: p.roas >= 1.3 ? "#3B6D11" : p.roas > 0 ? "#854F0B" : "#aaa" }}>
                      {p.roas > 0 ? p.roas.toFixed(2) : <span style={{ color:"#bbb", fontSize:11 }}>Sin campaña activa</span>}
                    </td>

                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee" }}>{fmtMargen(p.margen_real)}</td>
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee" }}>{fmtMargen(p.margen_potencial)}</td>

                    {/* Escenario ácido */}
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee" }}>
                      <BadgeEscenario p={p} />
                    </td>

                    {/* Acción con tooltip */}
                    <td style={{ padding: "8px 6px 8px 0", borderBottom: "0.5px solid #f0f0ee" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:2 }}>
                        <span style={{ ...pillStyle[accion], fontSize: 10, padding: "2px 8px",
                          borderRadius: 20, fontWeight: 500, whiteSpace: "nowrap" }}>
                          {pillLabel[accion] || "—"}
                        </span>
                        <TooltipAccion accion={accion} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>
          {productosFiltrados.length} de {productos.length} productos
        </div>
      </div>
    </div>
  );
}
